import type { KundenZeile } from '../../../lib/apiStatsApi';
import { deltaAnzeige, deltaFarbe, formatQuote, formatZeitpunkt } from './format';
import { nutzeSortierung, SortierKopf, type Spalte } from './Tabellen';

interface KundenTabelleProps {
  zeilen: KundenZeile[];
  gekuerzt: boolean;
  darkMode: boolean;
  onWaehleMandant: (wert: string) => void;
}

function mandantWert(z: KundenZeile): string {
  if (z.mandant.typ === 'team') return `t${z.mandant.id}`;
  if (z.mandant.typ === 'user') return `u${z.mandant.id}`;
  return 'ohne';
}

export default function KundenTabelle({ zeilen, gekuerzt, darkMode, onWaehleMandant }: KundenTabelleProps) {
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  const spalten: Spalte<KundenZeile>[] = [
    { key: 'kunde', titel: 'Kunde', wert: z => z.mandant.name },
    { key: 'aufrufe', titel: 'Aufrufe', rechts: true, wert: z => z.aufrufe },
    { key: 'fehler', titel: 'Fehler', rechts: true, wert: z => z.fehler },
    { key: 'quote', titel: 'Fehlerquote', rechts: true, wert: z => z.fehlerquote, anzeige: z => formatQuote(z.fehlerquote) },
    { key: 'schluessel', titel: 'Schlüssel', rechts: true, wert: z => z.schluessel },
    { key: 'zugriff', titel: 'Letzter Zugriff', rechts: true, wert: z => z.letzterZugriff, anzeige: z => formatZeitpunkt(z.letzterZugriff) },
    {
      key: 'delta', titel: 'Δ Vorperiode', rechts: true,
      wert: z => (z.vorherAufrufe === 0 ? (z.aufrufe > 0 ? Number.POSITIVE_INFINITY : 0) : (z.aufrufe - z.vorherAufrufe) / z.vorherAufrufe),
      anzeige: z => {
        const d = deltaAnzeige(z.aufrufe, z.vorherAufrufe);
        return <span className={deltaFarbe(d.richtung, darkMode)}>{d.text}</span>;
      },
    },
  ];

  const { sortierung, sortiere, umschalten } = nutzeSortierung(spalten, 'aufrufe');

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-sm">
        <SortierKopf spalten={spalten} sortierung={sortierung} umschalten={umschalten} darkMode={darkMode} />
        <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
          {sortiere(zeilen).map(z => (
            <tr
              key={mandantWert(z)}
              onClick={() => onWaehleMandant(mandantWert(z))}
              title="Auf diesen Kunden filtern"
              className={`cursor-pointer ${darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'} ${z.mandant.typ === 'ohne' ? muted : ''}`}
            >
              {spalten.map(s => (
                <td key={s.key} className={`px-4 py-2 ${s.rechts ? 'text-right' : 'text-left'} ${s.key === 'kunde' ? 'font-medium' : ''}`}>
                  {s.anzeige ? s.anzeige(z) : s.wert(z)}
                </td>
              ))}
            </tr>
          ))}
          {zeilen.length === 0 && (
            <tr><td colSpan={spalten.length} className={`px-4 py-6 text-center ${muted}`}>Keine Kunden mit API-Schlüsseln</td></tr>
          )}
        </tbody>
      </table>
      {gekuerzt && (
        <p className={`px-4 py-2 text-xs ${muted}`}>Auf 500 Zeilen gekürzt — Filter enger stellen.</p>
      )}
    </div>
  );
}
